import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { healthService, HealthProgramGroupDto, setCurrentUser } from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import FormInput from '../../components/Forms/FormInput';

const ProgramGroupCreate: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);
  const [formData, setFormData] = useState<HealthProgramGroupDto>({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user changes it
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const createdProgramGroup = await healthService.createProgramGroup(formData);
      navigate(`/health/program-groups/${createdProgramGroup.id}`);
    } catch (error) {
      console.error('Error creating program group:', error);
      // Could add error handling/notification here
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/health/program-groups');
  };

  return (
    <div>
      <PageHeader
        title="Create Program Group"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: "Create" }
        ]}
      />

      <Card className="mt-6">
        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          <FormInput
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            error={errors.name}
          />
          
          <FormInput
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            error={errors.description}
          />

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Program Group'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProgramGroupCreate;